import type {
  Alert,
  AlertAcknowledgeInput,
  AlertCreateInput,
  AlertDismissInput,
  AlertLinkWorkItemInput,
  AlertResolveInput,
  AlertStatus,
  CommandCreateInput,
  CommandStatusResponse,
  DomainEvent,
  ExternalIdMapping,
  IntegrationConnection,
  IntegrationConnectionCreateInput,
  IntegrationConnectionUpdateInput,
  IntegrationEvent,
  IntegrationForceRefreshResponse,
  IntegrationHealth,
  MappingBulkLinkInput,
  MappingCreateAndLinkInput,
  MappingLinkInput,
  MappingRejectInput,
  Page,
  UnmappedExternalEntity,
  WorkBoardResponse,
  WorkItem,
  WorkItemAssignInput,
  WorkItemCloseInput,
  WorkItemCreateInput,
  WorkItemMoveInput,
  WorkItemStatus,
  WorkItemUpdateInput,
} from "@conatus/contracts";
import { api } from "@/lib/api";

interface ListOptions {
  cursor?: string;
  limit?: number;
}

interface AlertListOptions extends ListOptions {
  status?: AlertStatus;
}

interface WorkListOptions extends ListOptions {
  status?: WorkItemStatus;
}

function withQuery(path: string, options?: object): string {
  if (!options) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(options)) {
    if (v === undefined || v === null || v === "") continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

const tenantBase = (tenantId: string): string =>
  `/tenants/${encodeURIComponent(tenantId)}`;

export const OperationsApi = {
  alerts: {
    list(tenantId: string, options?: AlertListOptions) {
      return api<Page<Alert>>(withQuery(`${tenantBase(tenantId)}/alerts`, options));
    },
    get(tenantId: string, alertId: string) {
      return api<Alert>(
        `${tenantBase(tenantId)}/alerts/${encodeURIComponent(alertId)}`,
      );
    },
    create(tenantId: string, input: AlertCreateInput) {
      return api<Alert>(`${tenantBase(tenantId)}/alerts`, {
        method: "POST",
        body: input,
      });
    },
    acknowledge(tenantId: string, alertId: string, input: AlertAcknowledgeInput) {
      return api<Alert>(
        `${tenantBase(tenantId)}/alerts/${encodeURIComponent(alertId)}/acknowledge`,
        { method: "POST", body: input },
      );
    },
    dismiss(tenantId: string, alertId: string, input: AlertDismissInput) {
      return api<Alert>(
        `${tenantBase(tenantId)}/alerts/${encodeURIComponent(alertId)}/dismiss`,
        { method: "POST", body: input },
      );
    },
    resolve(tenantId: string, alertId: string, input: AlertResolveInput) {
      return api<Alert>(
        `${tenantBase(tenantId)}/alerts/${encodeURIComponent(alertId)}/resolve`,
        { method: "POST", body: input },
      );
    },
    linkWorkItem(
      tenantId: string,
      alertId: string,
      input: AlertLinkWorkItemInput,
    ) {
      return api<{ alert: Alert; workItem: WorkItem }>(
        `${tenantBase(tenantId)}/alerts/${encodeURIComponent(alertId)}/link-work-item`,
        { method: "POST", body: input },
      );
    },
  },

  workItems: {
    list(tenantId: string, options?: WorkListOptions) {
      return api<Page<WorkItem>>(
        withQuery(`${tenantBase(tenantId)}/work-items`, options),
      );
    },
    board(tenantId: string, options?: ListOptions) {
      return api<WorkBoardResponse>(
        withQuery(`${tenantBase(tenantId)}/work-items/board`, options),
      );
    },
    create(tenantId: string, input: WorkItemCreateInput) {
      return api<WorkItem>(`${tenantBase(tenantId)}/work-items`, {
        method: "POST",
        body: input,
      });
    },
    update(tenantId: string, workItemId: string, input: WorkItemUpdateInput) {
      return api<WorkItem>(
        `${tenantBase(tenantId)}/work-items/${encodeURIComponent(workItemId)}`,
        { method: "PATCH", body: input },
      );
    },
    move(tenantId: string, workItemId: string, input: WorkItemMoveInput) {
      return api<WorkItem>(
        `${tenantBase(tenantId)}/work-items/${encodeURIComponent(workItemId)}/move`,
        { method: "POST", body: input },
      );
    },
    assign(tenantId: string, workItemId: string, input: WorkItemAssignInput) {
      return api<WorkItem>(
        `${tenantBase(tenantId)}/work-items/${encodeURIComponent(workItemId)}/assign`,
        { method: "POST", body: input },
      );
    },
    close(tenantId: string, workItemId: string, input: WorkItemCloseInput) {
      return api<WorkItem>(
        `${tenantBase(tenantId)}/work-items/${encodeURIComponent(workItemId)}/close`,
        { method: "POST", body: input },
      );
    },
  },

  commands: {
    listForDevice(tenantId: string, deviceId: string, options?: ListOptions) {
      return api<Page<CommandStatusResponse["request"]>>(
        withQuery(
          `${tenantBase(tenantId)}/devices/${encodeURIComponent(deviceId)}/commands`,
          options,
        ),
      );
    },
    createForDevice(tenantId: string, deviceId: string, input: CommandCreateInput) {
      return api<CommandStatusResponse>(
        `${tenantBase(tenantId)}/devices/${encodeURIComponent(deviceId)}/commands`,
        { method: "POST", body: input },
      );
    },
    getStatus(tenantId: string, commandId: string) {
      return api<CommandStatusResponse>(
        `${tenantBase(tenantId)}/commands/${encodeURIComponent(commandId)}`,
      );
    },
  },

  integrations: {
    list(tenantId: string, options?: ListOptions) {
      return api<Page<IntegrationConnection>>(
        withQuery(`${tenantBase(tenantId)}/integrations`, options),
      );
    },
    create(tenantId: string, input: IntegrationConnectionCreateInput) {
      return api<IntegrationConnection>(`${tenantBase(tenantId)}/integrations`, {
        method: "POST",
        body: input,
      });
    },
    update(
      tenantId: string,
      integrationId: string,
      input: IntegrationConnectionUpdateInput,
    ) {
      return api<IntegrationConnection>(
        `${tenantBase(tenantId)}/integrations/${encodeURIComponent(integrationId)}`,
        { method: "PATCH", body: input },
      );
    },
    health(tenantId: string, options?: ListOptions) {
      return api<Page<IntegrationHealth>>(
        withQuery(`${tenantBase(tenantId)}/integrations/health`, options),
      );
    },
    events(tenantId: string, integrationId: string, options?: ListOptions) {
      return api<Page<IntegrationEvent>>(
        withQuery(
          `${tenantBase(tenantId)}/integrations/${encodeURIComponent(integrationId)}/events`,
          options,
        ),
      );
    },
    forceRefresh(tenantId: string, integrationId: string) {
      return api<IntegrationForceRefreshResponse>(
        `${tenantBase(tenantId)}/integrations/${encodeURIComponent(integrationId)}/force-refresh`,
        { method: "POST" },
      );
    },
    disable(tenantId: string, integrationId: string) {
      return api<IntegrationConnection>(
        `${tenantBase(tenantId)}/integrations/${encodeURIComponent(integrationId)}/disable`,
        { method: "POST" },
      );
    },
    enable(tenantId: string, integrationId: string) {
      return api<IntegrationConnection>(
        `${tenantBase(tenantId)}/integrations/${encodeURIComponent(integrationId)}/enable`,
        { method: "POST" },
      );
    },
    get(tenantId: string, integrationId: string) {
      return api<IntegrationConnection>(
        `${tenantBase(tenantId)}/integrations/${encodeURIComponent(integrationId)}`,
      );
    },
    getMappings(tenantId: string, integrationId: string, options?: ListOptions) {
      return api<Page<ExternalIdMapping>>(
        withQuery(
          `${tenantBase(tenantId)}/integrations/${encodeURIComponent(integrationId)}/mappings`,
          options,
        ),
      );
    },
    linkMapping(
      tenantId: string,
      integrationId: string,
      input: MappingLinkInput,
    ) {
      return api<ExternalIdMapping>(
        `${tenantBase(tenantId)}/integrations/${encodeURIComponent(integrationId)}/mappings/link`,
        { method: "POST", body: input },
      );
    },
    createAndLinkMapping(
      tenantId: string,
      integrationId: string,
      input: MappingCreateAndLinkInput,
    ) {
      return api<ExternalIdMapping>(
        `${tenantBase(tenantId)}/integrations/${encodeURIComponent(integrationId)}/mappings/create-and-link`,
        { method: "POST", body: input },
      );
    },
    rejectMapping(
      tenantId: string,
      integrationId: string,
      input: MappingRejectInput,
    ) {
      return api<ExternalIdMapping>(
        `${tenantBase(tenantId)}/integrations/${encodeURIComponent(integrationId)}/mappings/reject`,
        { method: "POST", body: input },
      );
    },
    bulkLinkMappings(tenantId: string, input: MappingBulkLinkInput) {
      return api<{ mappings: ExternalIdMapping[] }>(
        `${tenantBase(tenantId)}/integrations/mappings/bulk-link`,
        { method: "POST", body: input },
      );
    },
    getUnmapped(tenantId: string, integrationId: string, options?: ListOptions) {
      return api<Page<UnmappedExternalEntity>>(
        withQuery(
          `${tenantBase(tenantId)}/integrations/${encodeURIComponent(integrationId)}/unmapped`,
          options,
        ),
      );
    },
  },

  events: {
    asset(tenantId: string, assetId: string, options?: ListOptions) {
      return api<Page<DomainEvent>>(
        withQuery(
          `${tenantBase(tenantId)}/assets/${encodeURIComponent(assetId)}/events`,
          options,
        ),
      );
    },
    device(tenantId: string, deviceId: string, options?: ListOptions) {
      return api<Page<DomainEvent>>(
        withQuery(
          `${tenantBase(tenantId)}/devices/${encodeURIComponent(deviceId)}/events`,
          options,
        ),
      );
    },
  },
};
